import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Ticket, 
  Plus, 
  Printer, 
  Users, 
  Package, 
  CheckCircle,
  Trash2,
  UserPlus,
  MapPin,
  Phone,
  FileText,
  Download
} from 'lucide-react';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PrePrintedPage() {
  const { getAuthHeader } = useAuth();
  const [batches, setBatches] = useState([]);
  const [agents, setAgents] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [agentDialogOpen, setAgentDialogOpen] = useState(false);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchTickets, setBatchTickets] = useState([]);
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  
  // Form states
  const [selectedEvent, setSelectedEvent] = useState('');
  const [quantity, setQuantity] = useState('100');
  const [batchName, setBatchName] = useState('');
  const [assignedAgent, setAssignedAgent] = useState('none');
  const [agentName, setAgentName] = useState('');
  const [agentPhone, setAgentPhone] = useState('');
  const [agentLocation, setAgentLocation] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [batchesRes, agentsRes, eventsRes] = await Promise.all([
        axios.get(`${API_URL}/api/batches`, getAuthHeader()),
        axios.get(`${API_URL}/api/agents`, getAuthHeader()),
        axios.get(`${API_URL}/api/events`, getAuthHeader())
      ]);
      setBatches(batchesRes.data);
      setAgents(agentsRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateBatch = async (e) => {
    e.preventDefault();
    if (!selectedEvent) {
      toast.error('Please select an event');
      return;
    }
    
    const qty = parseInt(quantity);
    if (qty < 1 || qty > 1000) {
      toast.error('Quantity must be between 1 and 1000');
      return;
    }

    setGenerating(true);
    setGenerationProgress(10);

    try {
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setGenerationProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const response = await axios.post(`${API_URL}/api/tickets/generate`, {
        event_id: selectedEvent,
        quantity: qty,
        ticket_type: 'physical',
        is_pre_printed: true,
        assigned_agent: assignedAgent === 'none' ? null : assignedAgent,
        batch_name: batchName || null
      }, getAuthHeader());
      
      clearInterval(progressInterval);
      setGenerationProgress(100);
      
      toast.success(`Batch created with ${response.data.length} pre-printed tickets!`);
      
      setTimeout(() => {
        fetchData();
        setGenerateDialogOpen(false);
        resetForm();
        setGenerating(false);
        setGenerationProgress(0);
      }, 500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate batch');
      setGenerating(false);
      setGenerationProgress(0);
    }
  };

  const handleCreateAgent = async (e) => {
    e.preventDefault();
    if (!agentName.trim()) {
      toast.error('Please enter agent name');
      return;
    }
    setSubmitting(true);

    try {
      await axios.post(`${API_URL}/api/agents`, {
        name: agentName,
        phone: agentPhone || null,
        location: agentLocation || null
      }, getAuthHeader());
      
      toast.success('Agent created successfully');
      fetchData();
      setAgentDialogOpen(false);
      setAgentName('');
      setAgentPhone('');
      setAgentLocation('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create agent');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAgent = async (agentId) => {
    if (!window.confirm('Are you sure you want to delete this agent?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/agents/${agentId}`, getAuthHeader());
      toast.success('Agent deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete agent');
    }
  };

  const handleViewBatch = async (batch) => {
    setSelectedBatch(batch);
    try {
      const response = await axios.get(`${API_URL}/api/tickets?batch_id=${batch.id}`, getAuthHeader());
      setBatchTickets(response.data);
      setSelectedTickets(response.data.map(t => t.ticket_id));
      setPrintDialogOpen(true);
    } catch (error) {
      toast.error('Failed to fetch batch tickets');
    }
  };

  const handleMarkPrinted = async (batchId) => {
    try {
      await axios.post(`${API_URL}/api/batches/${batchId}/mark-printed`, {}, getAuthHeader());
      toast.success('Batch marked as printed');
      fetchData();
      if (selectedBatch?.id === batchId) {
        setSelectedBatch(prev => ({ ...prev, printed_at: new Date().toISOString() }));
      }
    } catch (error) {
      toast.error('Failed to update batch');
    }
  };

  const handleDeleteBatch = async (batchId) => {
    if (!window.confirm('Are you sure you want to delete this batch and all its tickets?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/batches/${batchId}`, getAuthHeader());
      toast.success('Batch and tickets deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete batch');
    }
  };

  // Sanitize string to prevent XSS
  const sanitizeString = (str) => {
    if (!str) return '';
    return String(str).replace(/[<>"'&]/g, (char) => {
      const entities = { '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;', '&': '&amp;' };
      return entities[char] || char;
    });
  };

  // Optimized bulk print for 80mm thermal printers
  const handleBulkPrint = () => {
    const ticketsToPrint = batchTickets.filter(t => selectedTickets.includes(t.ticket_id));
    
    if (ticketsToPrint.length === 0) {
      toast.error('No tickets selected');
      return;
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow popups to print tickets');
      return;
    }
    
    // Sanitize batch name for title
    const safeBatchName = sanitizeString(selectedBatch?.name || 'Batch');
    
    const doc = printWindow.document;
    doc.open();
    doc.write(`<!DOCTYPE html><html><head>`);
    doc.write(`<title>Bulk Print - ${safeBatchName} (${ticketsToPrint.length} tickets)</title>`);
    doc.write(`<style>
      @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;600;700&display=swap');
      
      * { margin: 0; padding: 0; box-sizing: border-box; }
      @page { size: 80mm auto; margin: 0; }
      body { font-family: 'JetBrains Mono', monospace; background: white; width: 80mm; margin: 0 auto; }
      .ticket { width: 76mm; padding: 4mm; margin: 0 auto; page-break-after: always; page-break-inside: avoid; }
      .ticket:last-child { page-break-after: auto; }
      .divider { text-align: center; font-size: 8pt; letter-spacing: 1px; color: #333; margin: 2mm 0; }
      .header { text-align: center; padding: 3mm 0; }
      .header h1 { font-size: 16pt; font-weight: 700; letter-spacing: 3px; text-transform: uppercase; }
      .section { margin: 3mm 0; }
      .section-label { font-size: 8pt; text-transform: uppercase; letter-spacing: 1px; color: #666; }
      .section-value { font-size: 12pt; font-weight: 700; margin-top: 1mm; }
      .ticket-id-section { text-align: center; margin: 4mm 0; padding: 2mm 0; }
      .ticket-id { font-size: 14pt; font-weight: 700; letter-spacing: 2px; }
      .qr-section { text-align: center; padding: 4mm 0; margin: 3mm 0; }
      .qr-code { width: 50mm; height: 50mm; margin: 0 auto; display: block; }
      .price-section { text-align: center; margin: 4mm 0; padding: 3mm 0; }
      .price { font-size: 18pt; font-weight: 700; }
      .footer { text-align: center; font-size: 8pt; color: #666; margin-top: 3mm; padding-top: 3mm; }
      @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } .ticket { page-break-after: always; } .ticket:last-child { page-break-after: auto; } }
    </style>`);
    doc.write('</head><body>');
    
    // Generate ticket HTML with sanitized values
    ticketsToPrint.forEach((ticket, index) => {
      doc.write(`
        <div class="ticket">
          <div class="divider">============================</div>
          <div class="header"><h1>GAM TICKET</h1></div>
          <div class="divider">============================</div>
          <div class="section"><div class="section-label">EVENT:</div><div class="section-value">${sanitizeString(ticket.event_name)}</div></div>
          <div class="section"><div class="section-label">DATE:</div><div class="section-value">${sanitizeString(ticket.date)}</div></div>
          <div class="section"><div class="section-label">VENUE:</div><div class="section-value">${sanitizeString(ticket.venue)}</div></div>
          <div class="divider">----------------------------</div>
          <div class="ticket-id-section"><div class="ticket-id">TICKET ID: ${sanitizeString(ticket.ticket_id)}</div></div>
          <div class="divider">----------------------------</div>
          <div class="qr-section"><img class="qr-code" id="qr-${index}" /></div>
          <div class="divider">----------------------------</div>
          <div class="price-section"><div class="price">PRICE: D${sanitizeString(ticket.price)}</div></div>
          <div class="divider">----------------------------</div>
          <div class="footer">Powered by Gam Ticket</div>
        </div>
      `);
    });
    
    // Add QR code generation script with sanitized ticket IDs
    const safeTicketIds = ticketsToPrint.map(t => sanitizeString(t.ticket_id));
    doc.write(`
      <script src="https://cdn.jsdelivr.net/npm/qrcode@1.5.3/build/qrcode.min.js"><\/script>
      <script>
        const tickets = ${JSON.stringify(safeTicketIds)};
        function generateQRCodes() {
          tickets.forEach((ticketId, index) => {
            const img = document.getElementById('qr-' + index);
            QRCode.toDataURL(ticketId, { width: 200, margin: 1, errorCorrectionLevel: 'H' }, function(err, url) {
              if (!err) img.src = url;
            });
          });
          setTimeout(() => window.print(), 1000);
        }
        generateQRCodes();
      <\/script>
    `);
    doc.write('</body></html>');
    doc.close();
  };

  const toggleTicketSelection = (ticketId) => {
    setSelectedTickets(prev => 
      prev.includes(ticketId) 
        ? prev.filter(id => id !== ticketId)
        : [...prev, ticketId]
    );
  };

  const selectAllTickets = () => {
    setSelectedTickets(batchTickets.map(t => t.ticket_id));
  };

  const deselectAllTickets = () => {
    setSelectedTickets([]);
  };

  const resetForm = () => {
    setSelectedEvent('');
    setQuantity('100');
    setBatchName('');
    setAssignedAgent('none');
  };

  const getAgentName = (agentId) => {
    const agent = agents.find(a => a.id === agentId);
    return agent ? agent.name : 'Unassigned';
  };

  const getEventAvailableTickets = (eventId) => {
    const event = events.find(e => e.id === eventId);
    if (!event) return 0;
    return event.total_tickets - event.tickets_sold;
  };

  // Calculate batch statistics
  const totalBatches = batches.length;
  const totalPrePrintedTickets = batches.reduce((sum, b) => sum + b.quantity, 0);
  const printedBatches = batches.filter(b => b.printed_at).length;

  return (
    <Layout title="Pre-Printed Tickets">
      <p className="text-muted-foreground mb-6">Bulk generate and manage pre-printed tickets for distribution</p>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="border border-border bg-white">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Total Batches</p>
                <p className="font-heading text-3xl font-bold">{totalBatches}</p>
              </div>
              <div className="bg-[#0055FF] w-10 h-10 rounded flex items-center justify-center">
                <Package className="w-5 h-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-border bg-white">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Pre-Printed Tickets</p>
                <p className="font-heading text-3xl font-bold">{totalPrePrintedTickets}</p>
              </div>
              <div className="bg-[#00C853] w-10 h-10 rounded flex items-center justify-center">
                <Ticket className="w-5 h-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-border bg-white">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Printed Batches</p>
                <p className="font-heading text-3xl font-bold">{printedBatches}/{totalBatches}</p>
              </div>
              <div className="bg-[#FFB300] w-10 h-10 rounded flex items-center justify-center">
                <Printer className="w-5 h-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="batches" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="batches" className="gap-2" data-testid="batches-tab">
            <Package className="w-4 h-4" />
            Batches
          </TabsTrigger>
          <TabsTrigger value="agents" className="gap-2" data-testid="agents-tab">
            <Users className="w-4 h-4" />
            Distribution Agents
          </TabsTrigger>
        </TabsList>

        {/* Batches Tab */}
        <TabsContent value="batches">
          <div className="flex justify-end mb-4">
            <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="create-batch-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Generate Bulk Tickets
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl font-bold">
                    Bulk Ticket Generation
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleGenerateBatch} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wide font-medium">Select Event</Label>
                    <Select value={selectedEvent} onValueChange={setSelectedEvent}>
                      <SelectTrigger data-testid="batch-event-select">
                        <SelectValue placeholder="Choose an event" />
                      </SelectTrigger>
                      <SelectContent>
                        {events.map((event) => (
                          <SelectItem key={event.id} value={event.id}>
                            {event.name} ({event.total_tickets - event.tickets_sold} available)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedEvent && (
                      <p className="text-xs text-muted-foreground">
                        {getEventAvailableTickets(selectedEvent)} tickets available
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wide font-medium">
                      Batch Name <span className="text-muted-foreground">(Optional)</span>
                    </Label>
                    <Input
                      placeholder="e.g., BATCH-GATE1-APR or auto-generated"
                      value={batchName}
                      onChange={(e) => setBatchName(e.target.value)}
                      data-testid="batch-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wide font-medium">
                      Number of Tickets (1-1000)
                    </Label>
                    <Input
                      type="number"
                      min="1"
                      max="1000"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      data-testid="batch-quantity-input"
                    />
                    <div className="flex gap-2 mt-2">
                      {[50, 100, 200, 500].map(num => (
                        <Button
                          key={num}
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setQuantity(num.toString())}
                          className={quantity === num.toString() ? 'border-[#0055FF] text-[#0055FF]' : ''}
                        >
                          {num}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wide font-medium">
                      Assign to Agent <span className="text-muted-foreground">(Optional)</span>
                    </Label>
                    <Select value={assignedAgent} onValueChange={setAssignedAgent}>
                      <SelectTrigger data-testid="batch-agent-select">
                        <SelectValue placeholder="Select agent" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Assignment</SelectItem>
                        {agents.map((agent) => (
                          <SelectItem key={agent.id} value={agent.id}>
                            {agent.name} {agent.location ? `(${agent.location})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {generating && (
                    <div className="space-y-2">
                      <Progress value={generationProgress} className="h-2" />
                      <p className="text-xs text-center text-muted-foreground">
                        Generating {quantity} tickets...
                      </p>
                    </div>
                  )}

                  <div className="flex gap-3 pt-4">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setGenerateDialogOpen(false)} 
                      className="flex-1"
                      disabled={generating}
                    >
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={generating || !selectedEvent} 
                      className="flex-1 bg-[#0055FF] hover:bg-[#0044CC]"
                      data-testid="confirm-create-batch-button"
                    >
                      {generating ? 'Generating...' : `Generate ${quantity} Tickets`}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card className="border border-border bg-white">
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="w-8 h-8 border-2 border-[#0055FF] border-t-transparent rounded-full animate-spin mx-auto"></div>
                </div>
              ) : batches.length === 0 ? (
                <div className="py-12 text-center">
                  <Package className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-lg font-medium mb-2">No Batches Created</p>
                  <p className="text-muted-foreground mb-4">Generate bulk tickets for pre-printing</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="font-semibold text-xs uppercase tracking-wide">Batch ID</TableHead>
                        <TableHead className="font-semibold text-xs uppercase tracking-wide">Event</TableHead>
                        <TableHead className="font-semibold text-xs uppercase tracking-wide text-center">Tickets</TableHead>
                        <TableHead className="font-semibold text-xs uppercase tracking-wide">Assigned To</TableHead>
                        <TableHead className="font-semibold text-xs uppercase tracking-wide">Status</TableHead>
                        <TableHead className="font-semibold text-xs uppercase tracking-wide text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {batches.map((batch) => (
                        <TableRow key={batch.id} data-testid={`batch-row-${batch.id}`}>
                          <TableCell>
                            <span className="font-mono font-semibold">{batch.name}</span>
                          </TableCell>
                          <TableCell>{batch.event_name}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline" className="font-mono">{batch.quantity}</Badge>
                          </TableCell>
                          <TableCell>
                            {batch.assigned_agent ? (
                              <div className="flex items-center gap-2">
                                <Users className="w-4 h-4 text-muted-foreground" />
                                {getAgentName(batch.assigned_agent)}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {batch.printed_at ? (
                              <Badge className="bg-[#00C853] hover:bg-[#00C853]">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Printed
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-[#FFB300] border-[#FFB300]">
                                Ready to Print
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => handleViewBatch(batch)}
                                className="bg-[#0055FF] hover:bg-[#0044CC]"
                                data-testid={`print-batch-${batch.id}`}
                              >
                                <Printer className="w-4 h-4 mr-1" />
                                Print All
                              </Button>
                              {!batch.printed_at && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDeleteBatch(batch.id)}
                                  className="text-destructive hover:text-destructive"
                                  data-testid={`delete-batch-${batch.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Agents Tab */}
        <TabsContent value="agents">
          <div className="flex justify-end mb-4">
            <Dialog open={agentDialogOpen} onOpenChange={setAgentDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="create-agent-button">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Distribution Agent
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl font-bold">Add Distribution Agent</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateAgent} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wide font-medium">Agent Name</Label>
                    <Input
                      placeholder="Enter agent name"
                      value={agentName}
                      onChange={(e) => setAgentName(e.target.value)}
                      required
                      data-testid="agent-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wide font-medium">Phone (Optional)</Label>
                    <Input
                      placeholder="Enter phone number"
                      value={agentPhone}
                      onChange={(e) => setAgentPhone(e.target.value)}
                      data-testid="agent-phone-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wide font-medium">Location (Optional)</Label>
                    <Input
                      placeholder="e.g., Gate 1, Stadium West"
                      value={agentLocation}
                      onChange={(e) => setAgentLocation(e.target.value)}
                      data-testid="agent-location-input"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setAgentDialogOpen(false)} className="flex-1">
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={submitting} 
                      className="flex-1 bg-[#0055FF] hover:bg-[#0044CC]"
                      data-testid="confirm-create-agent-button"
                    >
                      {submitting ? 'Adding...' : 'Add Agent'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {agents.length === 0 ? (
              <Card className="col-span-full border border-border bg-white">
                <CardContent className="py-12 text-center">
                  <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-lg font-medium mb-2">No Agents Yet</p>
                  <p className="text-muted-foreground">Add agents to track ticket distribution</p>
                </CardContent>
              </Card>
            ) : (
              agents.map((agent) => (
                <Card key={agent.id} className="border border-border bg-white" data-testid={`agent-card-${agent.id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <CardTitle className="font-heading text-lg font-bold">{agent.name}</CardTitle>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteAgent(agent.id)}
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        data-testid={`delete-agent-${agent.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      {agent.phone && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Phone className="w-4 h-4" />
                          <span>{agent.phone}</span>
                        </div>
                      )}
                      {agent.location && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <MapPin className="w-4 h-4" />
                          <span>{agent.location}</span>
                        </div>
                      )}
                    </div>
                    <div className="mt-4 pt-4 border-t border-border">
                      <div className="flex items-center justify-between">
                        <span className="text-xs uppercase tracking-wide text-muted-foreground">Tickets Assigned</span>
                        <Badge className="bg-[#0055FF] font-mono">{agent.tickets_assigned}</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Bulk Print Dialog */}
      <Dialog open={printDialogOpen} onOpenChange={setPrintDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold flex items-center gap-2">
              <Printer className="w-5 h-5" />
              Bulk Print: {selectedBatch?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Batch Info */}
            <div className="bg-muted/50 p-4 rounded-lg">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Event</p>
                  <p className="font-semibold">{selectedBatch?.event_name}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Total Tickets</p>
                  <p className="font-semibold">{batchTickets.length}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Status</p>
                  {selectedBatch?.printed_at ? (
                    <Badge className="bg-[#00C853]">Printed</Badge>
                  ) : (
                    <Badge variant="outline">Ready to Print</Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Selection Controls */}
            <div className="flex items-center justify-between">
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={selectAllTickets}>
                  Select All
                </Button>
                <Button variant="outline" size="sm" onClick={deselectAllTickets}>
                  Clear Selection
                </Button>
              </div>
              <span className="text-sm font-medium">
                {selectedTickets.length} of {batchTickets.length} selected
              </span>
            </div>

            {/* Ticket List */}
            <div className="border rounded-lg max-h-[300px] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={selectedTickets.length === batchTickets.length}
                        onCheckedChange={(checked) => checked ? selectAllTickets() : deselectAllTickets()}
                      />
                    </TableHead>
                    <TableHead>Ticket ID</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {batchTickets.map((ticket) => (
                    <TableRow key={ticket.ticket_id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedTickets.includes(ticket.ticket_id)}
                          onCheckedChange={() => toggleTicketSelection(ticket.ticket_id)}
                          data-testid={`select-ticket-${ticket.ticket_id}`}
                        />
                      </TableCell>
                      <TableCell className="font-mono font-semibold">{ticket.ticket_id}</TableCell>
                      <TableCell>{ticket.event_name}</TableCell>
                      <TableCell>
                        <Badge 
                          className={ticket.status === 'UNUSED' 
                            ? 'bg-[#00C853] hover:bg-[#00C853]' 
                            : 'bg-gray-200 text-gray-700'
                          }
                        >
                          {ticket.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Print Info */}
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Print Info:</strong> Tickets are optimized for 80mm thermal printers. 
                Each ticket will be printed on a separate page with clear cut lines.
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setPrintDialogOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              {!selectedBatch?.printed_at && (
                <Button
                  variant="outline"
                  onClick={() => handleMarkPrinted(selectedBatch.id)}
                  className="flex-1"
                  data-testid="mark-printed-button"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Mark as Printed
                </Button>
              )}
              <Button
                onClick={handleBulkPrint}
                disabled={selectedTickets.length === 0}
                className="flex-1 bg-[#0055FF] hover:bg-[#0044CC]"
                data-testid="print-selected-button"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print {selectedTickets.length} Tickets
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
